"use client";

/**
 * MultiGenreInput — tag-style input for selecting multiple genres.
 * Now backed by `useUnifiedGenres` — the single source of truth across
 * artists / tracks / releases / users / events. Genres are shown as
 * removable badges. The text input has autocomplete from the canonical
 * `genres` Firestore collection (seeded from src/lib/genres.ts).
 *
 * When the user types a value that isn't in the list and presses Enter / ,
 * the entry is persisted to the canonical store via `add()` so every other
 * input on the page (and the next page they open) sees it. Marked
 * `userSubmitted: true` server-side so admin can review under Super
 * Admin → Config Lists → Genres.
 */

import { useRef, useState, KeyboardEvent, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { useUnifiedGenres } from "@/hooks/use-unified-genres";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";

interface MultiGenreInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  maxGenres?: number;
}

export default function MultiGenreInput({
  values,
  onChange,
  placeholder = "Search or type a genre…",
  className,
  maxGenres = 10,
}: MultiGenreInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const { items: suggestions, add: persistGenre } = useUnifiedGenres();
  const t = useTranslations("common");

  const addGenre = useCallback((raw: string) => {
    const genre = raw.trim();
    if (!genre || values.includes(genre) || values.length >= maxGenres) {
      setInputValue("");
      setShowDropdown(false);
      return;
    }
    onChange([...values, genre]);
    setInputValue("");
    setShowDropdown(false);
    // Persist any new entry to the canonical Firestore store so the next
    // input on any page picks it up. No-op when the entry already exists.
    if (!suggestions.includes(genre)) {
      void persistGenre(genre);
    }
  }, [values, onChange, maxGenres, suggestions, persistGenre]);

  const removeGenre = (genre: string) => {
    onChange(values.filter((g) => g !== genre));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addGenre(inputValue);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    } else if (e.key === "Backspace" && !inputValue && values.length > 0) {
      removeGenre(values[values.length - 1]);
    }
  };

  const handleChange = (v: string) => {
    if (v.includes(",")) {
      const parts = v.split(",");
      parts.slice(0, -1).forEach((p) => addGenre(p));
      setInputValue(parts[parts.length - 1]);
    } else {
      setInputValue(v);
      setShowDropdown(true);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        if (inputValue.trim()) addGenre(inputValue);
      }
    }, 150);
  };

  const filtered = suggestions
    .filter((s) => !values.includes(s) && s.toLowerCase().includes(inputValue.toLowerCase()))
    .slice(0, 10);

  const canAdd = values.length < maxGenres;

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          "flex flex-wrap gap-1.5 rounded-md border-2 border-input bg-background px-3 py-2 min-h-9 cursor-text",
          className,
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {values.map((genre) => (
          <Badge
            key={genre}
            variant="secondary"
            className="flex items-center gap-1 text-xs px-2 py-0.5 h-6"
          >
            {genre}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeGenre(genre); }}
              className="ml-0.5 rounded-full hover:bg-muted-foreground/20 transition-colors"
              aria-label={`Remove ${genre}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}

        {canAdd && (
          <input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowDropdown(true)}
            onBlur={handleBlur}
            placeholder={values.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[100px] bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            autoComplete="off"
          />
        )}
      </div>

      {showDropdown && (filtered.length > 0 || (inputValue.trim() && !suggestions.some(s => s.toLowerCase() === inputValue.trim().toLowerCase()))) && (
        <ul className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-48 overflow-y-auto">
          {filtered.map((g) => (
            <li key={g}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); addGenre(g); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                {g}
              </button>
            </li>
          ))}
          {/* Not on the list yet: Enter adds it to BB's genres (Gordon 2026-11-03: "not fetching genres"). */}
          {inputValue.trim() && !suggestions.some(s => s.toLowerCase() === inputValue.trim().toLowerCase()) && (
            <li>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); addGenre(inputValue); }}
                className="w-full border-t border-border px-3 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                {t("addValue", { value: inputValue.trim() })}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
